import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { registerEmployee, getUsersByRole, getCurrentUser, getUserData } from '../../../services/authService'

const EmployeeRegister = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    employeeId: '',
    department: '',
    reportsTo: '',  // Manager's employeeId
    password: '',
    confirmPassword: ''
  })
  const [managers, setManagers] = useState([])  // Super admin list
  const [loading, setLoading] = useState(false)
  const [loadingManagers, setLoadingManagers] = useState(true)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // CRITICAL FIX 2: Role check - redirect if not super_admin
  useEffect(() => {
    const checkAccess = async () => {
      const currentUser = getCurrentUser()
      if (!currentUser) {
        navigate('/login')
        return
      }

      const userDataResult = await getUserData(currentUser.uid)
      if (!userDataResult.success || userDataResult.data.role !== 'super_admin') {
        navigate('/dashboard')  // Redirect non-admins
        return
      }

      // If super_admin, fetch managers
      fetchManagers()
    }

    checkAccess()
  }, [navigate])

  // Fetch managers (super_admins) for dropdown
  const fetchManagers = async () => {
    setLoadingManagers(true)
    const result = await getUsersByRole('super_admin')
    if (result.success) {
      setManagers(result.data)
    } else {
      setError('Failed to load managers. Please refresh the page.')
    }
    setLoadingManagers(false)
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('') // Clear error when user types
    setSuccessMessage('')  // Clear success when editing
  }

  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      employeeId: '',
      department: '',
      reportsTo: '',
      password: '',
      confirmPassword: ''
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    if (!formData.reportsTo) {
      setError('Please select a manager (Reports To)')
      return
    }

    setLoading(true)

    // Find selected manager's fullName
    const selectedManager = managers.find(m => m.employeeId === formData.reportsTo)

    const result = await registerEmployee(
      formData.email,
      formData.password,
      formData.fullName,
      formData.employeeId,
      formData.department,
      formData.reportsTo,  // Manager's employeeId
      selectedManager?.fullName || ''  // Manager's fullName
    )

    setLoading(false)

    if (result.success) {
      // CRITICAL FIX 5: Clear form and show success, don't navigate
      setSuccessMessage(`Employee ${formData.fullName} registered successfully!`)
      resetForm()
      // Don't navigate - allow admin to register multiple employees
    } else {
      setError(result.error)
    }
  }

  return (
    <div className="psyber-container psyber-portal-container">
      {/* Background Effects */}
      <div className="psyber-bg-effects">
        <div className="psyber-orb psyber-orb-1"></div>
        <div className="psyber-orb psyber-orb-2"></div>
        <div className="psyber-grid"></div>
      </div>

      {/* Registration Card */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh'
      }}>
        <div className="psyber-card" style={{
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          {/* Logo/Title */}
          <div style={{ marginBottom: '2rem' }}>
            <h1 className="psyber-title-gradient" style={{ marginBottom: '0.5rem' }}>
              Register Employee
            </h1>
            <p style={{ color: 'var(--psyber-text-secondary)', fontSize: 'var(--psyber-font-size-sm)' }}>
              Create a new employee account for PsyberNova Portal
            </p>
          </div>

          {/* Info Notice */}
          <div style={{
            padding: '0.75rem',
            marginBottom: '1.5rem',
            background: 'var(--psyber-info-bg)',
            color: 'var(--psyber-info)',
            borderRadius: 'var(--psyber-radius-md)',
            border: '1px solid var(--psyber-info)',
            fontSize: 'var(--psyber-font-size-sm)',
            textAlign: 'left'
          }}>
            <i className="fas fa-info-circle"></i> <strong>Admin Function:</strong> Creating employee accounts with manager assignments
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
            {/* Error Message */}
            {error && (
              <div style={{
                padding: '0.75rem',
                marginBottom: '1rem',
                background: 'var(--psyber-error-bg)',
                color: 'var(--psyber-error)',
                borderRadius: 'var(--psyber-radius-md)',
                border: '1px solid var(--psyber-error)',
                fontSize: 'var(--psyber-font-size-sm)'
              }}>
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div style={{
                padding: '0.75rem',
                marginBottom: '1rem',
                background: 'var(--psyber-success-bg)',
                color: 'var(--psyber-success)',
                borderRadius: 'var(--psyber-radius-md)',
                border: '1px solid var(--psyber-success)',
                fontSize: 'var(--psyber-font-size-sm)'
              }}>
                <i className="fas fa-check-circle"></i> {successMessage}
              </div>
            )}

            {/* Full Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="fullName" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                className="psyber-input"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={handleChange}
                required
                autoComplete="name"
              />
            </div>

            {/* Employee ID */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="employeeId" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Employee ID *
              </label>
              <input
                type="text"
                id="employeeId"
                name="employeeId"
                className="psyber-input"
                placeholder="EMP-001"
                value={formData.employeeId}
                onChange={handleChange}
                required
              />
            </div>

            {/* Department */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="department" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Department *
              </label>
              <input
                type="text"
                id="department"
                name="department"
                className="psyber-input"
                placeholder="Engineering, HR, Sales, etc."
                value={formData.department}
                onChange={handleChange}
                required
              />
            </div>

            {/* Reports To - Dropdown */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="reportsTo" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Reports To (Manager) *
              </label>
              <select
                id="reportsTo"
                name="reportsTo"
                className="psyber-input"
                value={formData.reportsTo}
                onChange={handleChange}
                required
                disabled={loadingManagers}
                style={{ cursor: loadingManagers ? 'wait' : 'pointer' }}
              >
                <option value="">-- Select Manager --</option>
                {managers.map((manager) => (
                  <option key={manager.employeeId} value={manager.employeeId}>
                    {manager.fullName} ({manager.employeeId})
                  </option>
                ))}
              </select>
              {loadingManagers && (
                <p style={{
                  marginTop: '0.25rem',
                  fontSize: 'var(--psyber-font-size-xs)',
                  color: 'var(--psyber-text-secondary)'
                }}>
                  Loading managers...
                </p>
              )}
            </div>

            {/* Email */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="email" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="psyber-input"
                placeholder="employee@company.com"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="password" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Password *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className="psyber-input"
                placeholder="Minimum 6 characters"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                minLength={6}
              />
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="confirmPassword" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Confirm Password *
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className="psyber-input"
                placeholder="Re-enter password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="psyber-btn psyber-btn-primary"
              disabled={loading || loadingManagers}
              style={{ width: '100%' }}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Registering Employee...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus"></i>
                  Register Employee
                </>
              )}
            </button>
          </form>

          {/* Footer Links */}
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid var(--psyber-card-border)',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <p>
              <Link to="/dashboard" style={{
                color: 'var(--psyber-primary)',
                fontWeight: 600
              }}>
                <i className="fas fa-arrow-left"></i> Back to Dashboard
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmployeeRegister
